import useHasServiceChoice from '@/hooks/useHasServiceChoice';
import useServiceStepDecision from '@/hooks/useServiceStepDecision';

/**
 * SVC-STEP — does THIS wizard have a Select Service step at all?
 *
 * ONE SOURCE, for the same reason `useHasServiceChoice` is one: every surface
 * that draws the wizard has to agree with the surface that navigates it.
 *
 * ⚠️ THE AXIS THAT WAS MISSED. QA SVC-F2 fixed the rail for the CATALOGUE
 * axis — a box with one live service shows no step — and `useHasServiceChoice`
 * became that shared answer. Increment 2 then added a SECOND way to skip:
 * `shouldShowStep` is false when a plan, province rule or account default has
 * already decided the service, and the navigation (AddNewOrder, EditOrder)
 * was taught it. The rail and the summary breadcrumb were NOT. So a merchant
 * with a saved default saw "Select Service" painted in the rail, was sent
 * straight from the form to the summary, and arrived at a summary that showed
 * the step as reached — the identical defect SVC-F2 named, on a new axis.
 *
 * Adding a third skip axis means changing THIS function, and every surface
 * follows. That is the whole point of it existing.
 *
 * `provinceCode` is the destination the order is going to, because the
 * decision depends on it: a province rule can decide the service for Karbala
 * and nothing else. Callers that own a live province (the two form pages) pass
 * theirs so the rail tracks the city as the merchant picks it; callers reading
 * a committed order pass the stored one.
 *
 * ⚠️ `stepKnownPresent` — PRESENCE IS A FACT, THE SKIP IS A PREDICTION, AND A
 * FACT OUTRANKS A PREDICTION (QA D3). Everything above answers "would the flow
 * route through the step?", which is false for a merchant whose account
 * default already decided. But that merchant can still REACH the step: the
 * summary's trace line carries a [Change] link precisely for them. Standing on
 * the step, the prediction is still false, so the rail rendered two steps and
 * highlighted SUMMARY while they were looking at Select Service — the lying
 * rail again, on the one population the link exists to serve. Only the page
 * that IS the step knows this, so it says so; the hook does not read the route,
 * because a route-aware hook is a hidden global.
 *
 * Same family as ServiceTraceLine's `chosenOnThisOrder`: an observed client
 * fact overrides an inferred server one.
 */
const useHasServiceStep = (
  provinceCode?: string | null,
  stepKnownPresent = false,
): boolean => {
  const hasServiceChoice = useHasServiceChoice();
  // Every merchant has this step in place of a carrier step (owner 2026-10-28).
  const { shouldShowStep } = useServiceStepDecision(provinceCode, true);

  return stepKnownPresent || (hasServiceChoice && shouldShowStep);
};

export default useHasServiceStep;
